import type { VariantProp } from '../../common/colors';
import type { CSS } from '../../stitches.config';
import { fade, palette } from '../../common/colors';
import { gradients } from '../../common/gradients';
import { outlineColors, outlines } from '../../common/outlines';
import { colors, darkThemeSelector, shadows } from '../../stitches.config';
import { selectors } from '../shared/styles';

export const labelColor = '$$labelColor';
export const iconColor = '$$iconColor';
export const arrowsColor = '$$arrowsColor';
export const loadingIconColor = '$$loadingIconColor';

const neutralCustomColors = {
  initial: '#2E3046',
  interactive: '#32344C',
};

type ControlStyleState = 'vividEnabled' | 'vividDisabled' | 'mutedEnabled' | 'mutedDisabled';

type FilledControlVariant = Exclude<VariantProp, 'neutral'>;

type OutlinePrefix = 'primary' | 'destructive' | 'alternative' | 'attention' | 'positive';

type FilledVariantConfig = {
  tokenSuffix: 'Primary' | 'Destructive' | 'Alternative' | 'Attention' | 'Positive';
  outlinePrefix: OutlinePrefix;
  useDestructiveShadows: boolean;
  colorScale: 'brand' | 'red' | 'blue' | 'amber' | 'green';
  mutedBgLight: string;
  mutedBgDark: string;
};

const FILLED_VARIANT_CONFIG: Record<FilledControlVariant, FilledVariantConfig> = {
  brand: {
    tokenSuffix: 'Primary',
    outlinePrefix: 'primary',
    useDestructiveShadows: false,
    colorScale: 'brand',
    mutedBgLight: palette.controlBgMutedPrimaryLight,
    mutedBgDark: palette.controlBgMutedPrimaryDark,
  },
  negative: {
    tokenSuffix: 'Destructive',
    outlinePrefix: 'destructive',
    useDestructiveShadows: true,
    colorScale: 'red',
    mutedBgLight: palette.controlBgMutedDestructiveLight,
    mutedBgDark: palette.controlBgMutedDestructiveDark,
  },
  alternative: {
    tokenSuffix: 'Alternative',
    outlinePrefix: 'alternative',
    useDestructiveShadows: false,
    colorScale: 'blue',
    mutedBgLight: palette.controlBgMutedAlternativeLight,
    mutedBgDark: palette.controlBgMutedAlternativeDark,
  },
  attention: {
    tokenSuffix: 'Attention',
    outlinePrefix: 'attention',
    useDestructiveShadows: false,
    colorScale: 'amber',
    mutedBgLight: palette.controlBgMutedAttentionLight,
    mutedBgDark: palette.controlBgMutedAttentionDark,
  },
  positive: {
    tokenSuffix: 'Positive',
    outlinePrefix: 'positive',
    useDestructiveShadows: false,
    colorScale: 'green',
    mutedBgLight: palette.controlBgMutedPositiveLight,
    mutedBgDark: palette.controlBgMutedPositiveDark,
  },
};

function scaleColor(scale: FilledVariantConfig['colorScale'], shade: number) {
  return colors[`${scale}${shade}` as keyof typeof colors];
}

function getOutlineColor(prefix: OutlinePrefix, state: string, theme: 'Light' | 'Dark'): string {
  const key = `${prefix}${state}${theme}` as keyof typeof outlineColors.control;
  return outlineColors.control[key];
}

function getVividBgColor(
  suffix: FilledVariantConfig['tokenSuffix'],
  state: string,
  theme: 'Light' | 'Dark',
) {
  const key = `controlBgVivid${suffix}${state}${theme}` as keyof typeof colors;
  return colors[key];
}

function getVividShadow(state: string, theme: 'Light' | 'Dark', useDestructiveShadows: boolean) {
  const family = useDestructiveShadows ? 'controlVividDestructive' : 'controlVividPrimary';
  const key = `${family}${state}${theme}` as keyof typeof shadows;
  return shadows[key];
}

function getMutedFocusedShadow(theme: 'Light' | 'Dark', useDestructiveShadows: boolean) {
  const family = useDestructiveShadows ? 'controlMutedDestructive' : 'controlMutedPrimary';
  const key = `${family}Focused${theme}` as keyof typeof shadows;
  return shadows[key];
}

function createFilledControlStyles(variant: FilledControlVariant) {
  const config = FILLED_VARIANT_CONFIG[variant];
  const {
    tokenSuffix,
    outlinePrefix,
    useDestructiveShadows,
    colorScale,
    mutedBgLight,
    mutedBgDark,
  } = config;
  const scale = colorScale;

  const vividEnabled: CSS = {
    backgroundColor: getVividBgColor(tokenSuffix, 'Initial', 'Light'),
    outline: outlines.control,
    outlineColor: getOutlineColor(outlinePrefix, 'VividInitial', 'Light'),
    boxShadow: getVividShadow('Initial', 'Light', useDestructiveShadows),
    [labelColor]: scaleColor(scale, 50),
    [iconColor]: scaleColor(scale, 100),
    [arrowsColor]: scaleColor(scale, 200),
    [loadingIconColor]: scaleColor(scale, 300),

    [darkThemeSelector]: {
      backgroundColor: getVividBgColor(tokenSuffix, 'Initial', 'Dark'),
      outlineColor: getOutlineColor(outlinePrefix, 'VividInitial', 'Dark'),
      boxShadow: getVividShadow('Initial', 'Dark', useDestructiveShadows),
    },

    [selectors.hover]: {
      backgroundColor: getVividBgColor(tokenSuffix, 'Hovered', 'Light'),
      outlineColor: getOutlineColor(outlinePrefix, 'VividHovered', 'Light'),
      boxShadow: getVividShadow('Hovered', 'Light', useDestructiveShadows),
      [labelColor]: colors.white,
      [iconColor]: scaleColor(scale, 50),
      [arrowsColor]: scaleColor(scale, 100),
      [loadingIconColor]: scaleColor(scale, 100),

      [darkThemeSelector]: {
        backgroundColor: getVividBgColor(tokenSuffix, 'Hovered', 'Dark'),
        outlineColor: getOutlineColor(outlinePrefix, 'VividHovered', 'Dark'),
        boxShadow: getVividShadow('Hovered', 'Dark', useDestructiveShadows),
      },
    },

    [selectors.focus]: {
      backgroundColor: getVividBgColor(tokenSuffix, 'Focused', 'Light'),
      outlineColor: getOutlineColor(outlinePrefix, 'VividFocused', 'Light'),
      boxShadow: getVividShadow('Focused', 'Light', useDestructiveShadows),

      [darkThemeSelector]: {
        backgroundColor: getVividBgColor(tokenSuffix, 'Focused', 'Dark'),
        outlineColor: getOutlineColor(outlinePrefix, 'VividFocused', 'Dark'),
        boxShadow: getVividShadow('Focused', 'Dark', useDestructiveShadows),
      },
    },
  };

  const vividDisabled: CSS = {
    backgroundColor: getVividBgColor(tokenSuffix, 'Disabled', 'Light'),
    outline: outlines.control,
    outlineColor: getOutlineColor(outlinePrefix, 'VividDisabled', 'Light'),
    boxShadow: getVividShadow('Disabled', 'Light', useDestructiveShadows),
    [labelColor]: scaleColor(scale, 200),
    [iconColor]: scaleColor(scale, 300),
    [arrowsColor]: scaleColor(scale, 400),
    [loadingIconColor]: scaleColor(scale, 400),

    [darkThemeSelector]: {
      backgroundColor: getVividBgColor(tokenSuffix, 'Disabled', 'Dark'),
      outlineColor: getOutlineColor(outlinePrefix, 'VividDisabled', 'Dark'),
      boxShadow: getVividShadow('Disabled', 'Dark', useDestructiveShadows),
    },
  };

  const mutedEnabled: CSS = {
    backgroundColor: fade(mutedBgLight, 0.5),
    outline: outlines.control,
    outlineColor: getOutlineColor(outlinePrefix, 'MutedInitial', 'Light'),
    [labelColor]: scaleColor(scale, 800),
    [iconColor]: scaleColor(scale, 700),
    [arrowsColor]: scaleColor(scale, 600),
    [loadingIconColor]: scaleColor(scale, 600),

    [darkThemeSelector]: {
      backgroundColor: fade(mutedBgDark, 0.5),
      outlineColor: getOutlineColor(outlinePrefix, 'MutedInitial', 'Dark'),
      [labelColor]: scaleColor(scale, 50),
      [iconColor]: scaleColor(scale, 100),
      [arrowsColor]: scaleColor(scale, 200),
      [loadingIconColor]: scaleColor(scale, 200),
    },

    [selectors.hover]: {
      backgroundColor: fade(mutedBgLight, 0.9),
      outlineColor: getOutlineColor(outlinePrefix, 'MutedHovered', 'Light'),
      [labelColor]: scaleColor(scale, 900),
      [iconColor]: scaleColor(scale, 800),
      [arrowsColor]: scaleColor(scale, 700),
      [loadingIconColor]: scaleColor(scale, 700),

      [darkThemeSelector]: {
        backgroundColor: fade(mutedBgDark, 0.7),
        outlineColor: getOutlineColor(outlinePrefix, 'MutedHovered', 'Dark'),
        [labelColor]: colors.white,
        [iconColor]: scaleColor(scale, 50),
        [arrowsColor]: scaleColor(scale, 100),
        [loadingIconColor]: scaleColor(scale, 100),
      },
    },

    [selectors.focus]: {
      backgroundColor: fade(mutedBgLight, 0.9),
      outlineColor: getOutlineColor(outlinePrefix, 'MutedFocused', 'Light'),
      boxShadow: getMutedFocusedShadow('Light', useDestructiveShadows),
      [labelColor]: scaleColor(scale, 900),
      [iconColor]: scaleColor(scale, 800),
      [arrowsColor]: scaleColor(scale, 700),
      [loadingIconColor]: scaleColor(scale, 700),

      [darkThemeSelector]: {
        backgroundColor: fade(mutedBgDark, 0.7),
        outlineColor: getOutlineColor(outlinePrefix, 'MutedFocused', 'Dark'),
        boxShadow: getMutedFocusedShadow('Dark', useDestructiveShadows),
        [labelColor]: colors.white,
        [iconColor]: scaleColor(scale, 50),
        [arrowsColor]: scaleColor(scale, 100),
        [loadingIconColor]: scaleColor(scale, 100),
      },
    },
  };

  const mutedDisabled: CSS = {
    backgroundColor: fade(mutedBgLight, 0.5),
    outline: outlines.control,
    outlineColor: getOutlineColor(outlinePrefix, 'MutedDisabled', 'Light'),
    [labelColor]: scaleColor(scale, 800),
    [iconColor]: scaleColor(scale, 700),
    [arrowsColor]: scaleColor(scale, 600),
    [loadingIconColor]: scaleColor(scale, 600),

    [darkThemeSelector]: {
      backgroundColor: fade(mutedBgDark, 0.5),
      outlineColor: getOutlineColor(outlinePrefix, 'MutedDisabled', 'Dark'),
      [labelColor]: scaleColor(scale, 50),
      [iconColor]: scaleColor(scale, 100),
      [arrowsColor]: scaleColor(scale, 200),
      [loadingIconColor]: scaleColor(scale, 200),
    },
  };

  return {
    vividEnabled,
    vividDisabled,
    mutedEnabled,
    mutedDisabled,
  };
}

const neutralStyles = {
  vividEnabled: {
    backgroundColor: palette.white,
    backgroundImage: gradients.controlSecondary,
    outline: outlines.control,
    outlineColor: outlineColors.control.secondaryVividInitialLight,
    boxShadow: shadows.controlSecondaryVividInitialLight,
    [labelColor]: colors.gray700,
    [iconColor]: colors.gray600,
    [arrowsColor]: colors.gray500,
    [loadingIconColor]: colors.gray500,

    [darkThemeSelector]: {
      backgroundColor: neutralCustomColors.initial,
      backgroundImage: gradients.controlSecondaryDark,
      outlineColor: outlineColors.control.secondaryVividInitialDark,
      boxShadow: shadows.controlSecondaryVividInitialDark,
      [labelColor]: colors.gray50,
      [iconColor]: colors.gray100,
      [arrowsColor]: colors.gray200,
      [loadingIconColor]: colors.gray200,
    },

    [selectors.hover]: {
      backgroundColor: palette.gray50,
      outlineColor: outlineColors.control.secondaryVividHoveredLight,
      boxShadow: shadows.controlSecondaryVividHoveredLight,
      [labelColor]: colors.gray800,
      [iconColor]: colors.gray700,
      [arrowsColor]: colors.gray600,
      [loadingIconColor]: colors.gray600,

      [darkThemeSelector]: {
        backgroundColor: neutralCustomColors.interactive,
        outlineColor: outlineColors.control.secondaryVividHoveredDark,
        boxShadow: shadows.controlSecondaryVividHoveredDark,
        [labelColor]: colors.white,
        [iconColor]: colors.gray50,
        [arrowsColor]: colors.gray100,
        [loadingIconColor]: colors.gray100,
      },
    },

    [selectors.focus]: {
      outlineColor: outlineColors.control.secondaryVividFocusedLight,
      boxShadow: shadows.controlSecondaryVividFocusedLight,

      [darkThemeSelector]: {
        outlineColor: outlineColors.control.secondaryVividFocusedDark,
        boxShadow: shadows.controlSecondaryVividFocusedDark,
      },
    },
  } satisfies CSS,
  vividDisabled: {
    backgroundColor: palette.white,
    backgroundImage: gradients.controlSecondary,
    outline: outlines.control,
    outlineColor: outlineColors.control.secondaryVividDisabledLight,
    boxShadow: shadows.controlSecondaryVividDisabledLight,
    [labelColor]: colors.gray400,
    [iconColor]: colors.gray300,
    [arrowsColor]: colors.gray200,
    [loadingIconColor]: colors.gray200,

    [darkThemeSelector]: {
      backgroundColor: colors.gray700,
      backgroundImage: gradients.controlSecondaryDark,
      outlineColor: outlineColors.control.secondaryVividDisabledDark,
      boxShadow: shadows.controlSecondaryVividDisabledDark,
      [labelColor]: colors.gray200,
      [iconColor]: colors.gray300,
      [arrowsColor]: colors.gray400,
      [loadingIconColor]: colors.gray400,
    },
  } satisfies CSS,
  mutedEnabled: {
    backgroundColor: colors.bgApplicationLight,
    outline: outlines.control,
    outlineColor: outlineColors.control.secondaryMutedInitialLight,
    [labelColor]: colors.gray600,
    [iconColor]: colors.gray500,
    [arrowsColor]: colors.gray400,
    [loadingIconColor]: colors.gray400,

    [darkThemeSelector]: {
      backgroundColor: colors.bgApplicationDark,
      outlineColor: outlineColors.control.secondaryMutedInitialDark,
      [labelColor]: colors.gray50,
      [iconColor]: colors.gray100,
      [arrowsColor]: colors.gray200,
      [loadingIconColor]: colors.gray200,
    },

    [selectors.hover]: {
      backgroundColor: colors.bgNeutralLight,
      outlineColor: outlineColors.control.secondaryMutedHoveredLight,
      [labelColor]: colors.gray800,
      [iconColor]: colors.gray700,
      [arrowsColor]: colors.gray600,
      [loadingIconColor]: colors.gray600,

      [darkThemeSelector]: {
        backgroundColor: colors.bgNeutralDark,
        outlineColor: outlineColors.control.secondaryMutedHoveredDark,
        [labelColor]: colors.white,
        [iconColor]: colors.gray50,
        [arrowsColor]: colors.gray100,
        [loadingIconColor]: colors.gray100,
      },
    },

    [selectors.focus]: {
      backgroundColor: colors.bgNeutralLight,
      outlineColor: outlineColors.control.secondaryMutedFocusedLight,
      boxShadow: shadows.controlSecondaryMutedFocusedLight,

      [darkThemeSelector]: {
        backgroundColor: colors.bgNeutralDark,
        outlineColor: outlineColors.control.secondaryMutedFocusedDark,
        boxShadow: shadows.controlSecondaryMutedFocusedDark,
      },
    },
  } satisfies CSS,
  mutedDisabled: {
    backgroundColor: colors.bgApplicationLight,
    outline: outlines.control,
    outlineColor: outlineColors.control.secondaryMutedDisabledLight,
    [labelColor]: colors.gray400,
    [iconColor]: colors.gray300,
    [arrowsColor]: colors.gray500,
    [loadingIconColor]: colors.gray500,

    [darkThemeSelector]: {
      backgroundColor: colors.bgApplicationDark,
      outlineColor: outlineColors.control.secondaryMutedDisabledDark,
      [labelColor]: colors.gray200,
      [iconColor]: colors.gray300,
      [arrowsColor]: colors.gray400,
      [loadingIconColor]: colors.gray400,
    },
  } satisfies CSS,
};

const filledVariantStyles = {
  alternative: createFilledControlStyles('alternative'),
  attention: createFilledControlStyles('attention'),
  brand: createFilledControlStyles('brand'),
  negative: createFilledControlStyles('negative'),
  positive: createFilledControlStyles('positive'),
};

export const controlVariantStyles: Record<VariantProp, Record<ControlStyleState, CSS>> = {
  alternative: filledVariantStyles.alternative,
  attention: filledVariantStyles.attention,
  brand: filledVariantStyles.brand,
  negative: filledVariantStyles.negative,
  neutral: neutralStyles,
  positive: filledVariantStyles.positive,
};

export const alternativeVividEnabledStyles = controlVariantStyles.alternative.vividEnabled;
export const alternativeVividDisabledStyles = controlVariantStyles.alternative.vividDisabled;
export const alternativeMutedEnabledStyles = controlVariantStyles.alternative.mutedEnabled;
export const alternativeMutedDisabledStyles = controlVariantStyles.alternative.mutedDisabled;

export const attentionVividEnabledStyles = controlVariantStyles.attention.vividEnabled;
export const attentionVividDisabledStyles = controlVariantStyles.attention.vividDisabled;
export const attentionMutedEnabledStyles = controlVariantStyles.attention.mutedEnabled;
export const attentionMutedDisabledStyles = controlVariantStyles.attention.mutedDisabled;

export const brandVividEnabledStyles = controlVariantStyles.brand.vividEnabled;
export const brandVividDisabledStyles = controlVariantStyles.brand.vividDisabled;
export const brandMutedEnabledStyles = controlVariantStyles.brand.mutedEnabled;
export const brandMutedDisabledStyles = controlVariantStyles.brand.mutedDisabled;

export const negativeVividEnabledStyles = controlVariantStyles.negative.vividEnabled;
export const negativeVividDisabledStyles = controlVariantStyles.negative.vividDisabled;
export const negativeMutedEnabledStyles = controlVariantStyles.negative.mutedEnabled;
export const negativeMutedDisabledStyles = controlVariantStyles.negative.mutedDisabled;

export const neutralVividEnabledStyles = controlVariantStyles.neutral.vividEnabled;
export const neutralVividDisabledStyles = controlVariantStyles.neutral.vividDisabled;
export const neutralMutedEnabledStyles = controlVariantStyles.neutral.mutedEnabled;
export const neutralMutedDisabledStyles = controlVariantStyles.neutral.mutedDisabled;

export const positiveVividEnabledStyles = controlVariantStyles.positive.vividEnabled;
export const positiveVividDisabledStyles = controlVariantStyles.positive.vividDisabled;
export const positiveMutedEnabledStyles = controlVariantStyles.positive.mutedEnabled;
export const positiveMutedDisabledStyles = controlVariantStyles.positive.mutedDisabled;

/** @deprecated Use `brandVividEnabledStyles` instead. */
export const primaryVividEnabledStyles = brandVividEnabledStyles;
/** @deprecated Use `brandVividDisabledStyles` instead. */
export const primaryVividDisabledStyles = brandVividDisabledStyles;
/** @deprecated Use `brandMutedEnabledStyles` instead. */
export const primaryMutedEnabledStyles = brandMutedEnabledStyles;
/** @deprecated Use `brandMutedDisabledStyles` instead. */
export const primaryMutedDisabledStyles = brandMutedDisabledStyles;

/** @deprecated Use `neutralVividEnabledStyles` instead. */
export const secondaryVividEnabledStyles = neutralVividEnabledStyles;
/** @deprecated Use `neutralVividDisabledStyles` instead. */
export const secondaryVividDisabledStyles = neutralVividDisabledStyles;
/** @deprecated Use `neutralMutedEnabledStyles` instead. */
export const secondaryMutedEnabledStyles = neutralMutedEnabledStyles;
/** @deprecated Use `neutralMutedDisabledStyles` instead. */
export const secondaryMutedDisabledStyles = neutralMutedDisabledStyles;

/** @deprecated Use `negativeVividEnabledStyles` instead. */
export const destructiveVividEnabledStyles = negativeVividEnabledStyles;
/** @deprecated Use `negativeVividDisabledStyles` instead. */
export const destructiveVividDisabledStyles = negativeVividDisabledStyles;
/** @deprecated Use `negativeMutedEnabledStyles` instead. */
export const destructiveMutedEnabledStyles = negativeMutedEnabledStyles;
/** @deprecated Use `negativeMutedDisabledStyles` instead. */
export const destructiveMutedDisabledStyles = negativeMutedDisabledStyles;

export const internalEnabledStyles = {
  outline: outlines.control,
  outlineColor: outlineColors.control.internalInitialLight,
  [labelColor]: colors.internalBodyLight,
  [iconColor]: colors.internalIconLight,
  [arrowsColor]: colors.internalIconLight,
  [loadingIconColor]: colors.internalIconLight,

  [darkThemeSelector]: {
    outlineColor: outlineColors.control.internalInitialDark,
    [labelColor]: colors.internalBodyDark,
    [iconColor]: colors.internalIconDark,
    [arrowsColor]: colors.internalIconDark,
    [loadingIconColor]: colors.internalIconDark,
  },

  [selectors.hover]: {
    outlineColor: outlineColors.control.internalHoveredLight,
    [labelColor]: colors.internalHeadingLight,
    [iconColor]: colors.internalBodyLight,
    [arrowsColor]: colors.internalBodyLight,
    [loadingIconColor]: colors.internalBodyLight,

    [darkThemeSelector]: {
      outlineColor: outlineColors.control.internalHoveredDark,
      [labelColor]: colors.internalHeadingDark,
      [iconColor]: colors.internalBodyDark,
      [arrowsColor]: colors.internalBodyDark,
      [loadingIconColor]: colors.internalBodyDark,
    },
  },

  [selectors.focus]: {
    outlineColor: outlineColors.control.internalFocusedLight,
    boxShadow: shadows.controlInternalFocusedLight,

    [darkThemeSelector]: {
      outlineColor: outlineColors.control.internalFocusedDark,
      boxShadow: shadows.controlInternalFocusedDark,
    },
  },
};

export const internalDisabledStyles = {
  outline: outlines.control,
  outlineColor: outlineColors.control.internalDisabledLight,
  [labelColor]: colors.internalBodyLight,
  [iconColor]: colors.internalIconLight,
  [arrowsColor]: colors.internalIconLight,
  [loadingIconColor]: colors.internalIconLight,

  [darkThemeSelector]: {
    outlineColor: outlineColors.control.internalDisabledDark,
    [labelColor]: colors.internalBodyDark,
    [iconColor]: colors.internalIconDark,
    [arrowsColor]: colors.internalIconDark,
    [loadingIconColor]: colors.internalIconDark,
  },
};

const interiorVariantColors: Record<
  VariantProp,
  {
    labelLight: string;
    iconLight: string;
    labelHoverLight: string;
    iconHoverLight: string;
    labelDark: string;
    iconDark: string;
    labelHoverDark: string;
    iconHoverDark: string;
  }
> = {
  alternative: {
    labelLight: palette.bodyAlternativeLight,
    iconLight: palette.iconAlternativeLight,
    labelHoverLight: palette.headingAlternativeLight,
    iconHoverLight: palette.bodyAlternativeLight,
    labelDark: palette.bodyAlternativeDark,
    iconDark: palette.iconAlternativeDark,
    labelHoverDark: palette.headingAlternativeDark,
    iconHoverDark: palette.bodyAlternativeDark,
  },
  attention: {
    labelLight: palette.bodyAttentionLight,
    iconLight: palette.iconAttentionLight,
    labelHoverLight: palette.headingAttentionLight,
    iconHoverLight: palette.bodyAttentionLight,
    labelDark: palette.bodyAttentionDark,
    iconDark: palette.iconAttentionDark,
    labelHoverDark: palette.headingAttentionDark,
    iconHoverDark: palette.bodyAttentionDark,
  },
  brand: {
    labelLight: palette.bodyBrandLight,
    iconLight: palette.iconBrandLight,
    labelHoverLight: palette.headingBrandLight,
    iconHoverLight: palette.bodyBrandLight,
    labelDark: palette.bodyBrandDark,
    iconDark: palette.iconBrandDark,
    labelHoverDark: palette.headingBrandDark,
    iconHoverDark: palette.bodyBrandDark,
  },
  negative: {
    labelLight: palette.bodyNegativeLight,
    iconLight: palette.iconNegativeLight,
    labelHoverLight: palette.headingNegativeLight,
    iconHoverLight: palette.bodyNegativeLight,
    labelDark: palette.bodyNegativeDark,
    iconDark: palette.iconNegativeDark,
    labelHoverDark: palette.headingNegativeDark,
    iconHoverDark: palette.bodyNegativeDark,
  },
  neutral: {
    labelLight: palette.bodyNeutralLight,
    iconLight: palette.iconNeutralLight,
    labelHoverLight: palette.headingNeutralLight,
    iconHoverLight: palette.bodyNeutralLight,
    labelDark: palette.bodyNeutralDark,
    iconDark: palette.iconNeutralDark,
    labelHoverDark: palette.headingNeutralDark,
    iconHoverDark: palette.bodyNeutralDark,
  },
  positive: {
    labelLight: palette.bodyPositiveLight,
    iconLight: palette.iconPositiveLight,
    labelHoverLight: palette.headingPositiveLight,
    iconHoverLight: palette.bodyPositiveLight,
    labelDark: palette.bodyPositiveDark,
    iconDark: palette.iconPositiveDark,
    labelHoverDark: palette.headingPositiveDark,
    iconHoverDark: palette.bodyPositiveDark,
  },
};

export function getInteriorVariantStyles(variant: VariantProp): CSS {
  const v = interiorVariantColors[variant];
  return {
    [labelColor]: v.labelLight,
    [iconColor]: v.iconLight,

    [darkThemeSelector]: {
      [labelColor]: v.labelDark,
      [iconColor]: v.iconDark,
    },

    [selectors.hover]: {
      [labelColor]: v.labelHoverLight,
      [iconColor]: v.iconHoverLight,

      [darkThemeSelector]: {
        [labelColor]: v.labelHoverDark,
        [iconColor]: v.iconHoverDark,
      },
    },
  };
}

export const CONTROL_VARIANT_KEYS: VariantProp[] = [
  'alternative',
  'attention',
  'brand',
  'negative',
  'neutral',
  'positive',
];

export function getControlVariantCompoundVariants(options?: { useSubmitSelector?: boolean }) {
  return CONTROL_VARIANT_KEYS.flatMap((variant) => [
    {
      appearance: 'vivid' as const,
      variant,
      isDisabled: false,
      css:
        options?.useSubmitSelector && variant === 'brand'
          ? {
              '&[type=button], &[type=submit]': controlVariantStyles[variant].vividEnabled,
              '&': controlVariantStyles[variant].vividEnabled,
            }
          : controlVariantStyles[variant].vividEnabled,
    },
    {
      appearance: 'vivid' as const,
      variant,
      isDisabled: true,
      css: controlVariantStyles[variant].vividDisabled,
    },
    {
      appearance: 'muted' as const,
      variant,
      isDisabled: false,
      css: controlVariantStyles[variant].mutedEnabled,
    },
    {
      appearance: 'muted' as const,
      variant,
      isDisabled: true,
      css: controlVariantStyles[variant].mutedDisabled,
    },
  ]);
}
